import { useCallback, useEffect, type ReactElement } from "react"
import { useRouter } from "next/router"

import { SignupLayout } from "@/app-shell/layouts/SignupLayout"
import { ClerkAuthFormSkeleton, ClerkSigninForm } from "@/signup"
import { useAuth } from "@clerk/nextjs"

import {
  buildClerkInviteHref,
  readClerkInviteStatusQuery,
  readClerkTicketQuery,
} from "../utils/signupQuery"

const CLERK_OAUTH_CONSENT_PATH = "/oauth-consent"

function readClerkAccountsPortalOrigin(): string | null {
  const publishableKey = process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY
  if (
    !publishableKey?.startsWith("pk_live_") &&
    !publishableKey?.startsWith("pk_test_")
  ) {
    return null
  }

  try {
    const encodedFrontendApi = publishableKey.split("_")[2]
    const decodedFrontendApi = atob(encodedFrontendApi)
    if (!decodedFrontendApi.endsWith("$")) {
      return null
    }

    const frontendApi = decodedFrontendApi.slice(0, -1)
    const accountsHost = frontendApi.startsWith("clerk.")
      ? `accounts.${frontendApi.slice("clerk.".length)}`
      : frontendApi.replace(".clerk.accounts.", ".accounts.")

    if (accountsHost === frontendApi) {
      return null
    }

    const accountsUrl = new URL(`https://${accountsHost}`)
    if (
      accountsUrl.hostname !== accountsHost ||
      accountsUrl.pathname !== "/" ||
      accountsUrl.port
    ) {
      return null
    }

    return accountsUrl.origin
  } catch {
    return null
  }
}

function readRedirectUrl(value: unknown): string {
  if (typeof value !== "string") {
    return "/home"
  }

  if (value.startsWith("/") && !value.startsWith("//")) {
    return value
  }

  try {
    const url = new URL(value)
    if (
      url.origin === readClerkAccountsPortalOrigin() &&
      url.pathname === CLERK_OAUTH_CONSENT_PATH
    ) {
      return value
    }
  } catch {
    // Invalid and untrusted redirect values fall back to the app home page.
  }

  return "/home"
}

const SigninRoute = () => {
  const router = useRouter()
  const { isLoaded, isSignedIn } = useAuth()

  const clerkTicket = readClerkTicketQuery(router.query.__clerk_ticket)
  const clerkInviteStatus = readClerkInviteStatusQuery(
    router.query.__clerk_status,
  )
  const isInviteRedirect = Boolean(
    clerkTicket &&
    (clerkInviteStatus === "sign_up" || clerkInviteStatus === "complete"),
  )
  const redirectUrl = clerkTicket
    ? "/home"
    : readRedirectUrl(router.query.redirect_url)
  const signUpHref = clerkTicket
    ? buildClerkInviteHref("/signup", clerkTicket, "sign_up")
    : "/signup"

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      void router.replace(redirectUrl)
    }
  }, [isLoaded, isSignedIn, redirectUrl, router])

  useEffect(() => {
    if (!isLoaded || isSignedIn || !router.isReady || !clerkTicket) {
      return
    }
    if (clerkInviteStatus === "sign_up") {
      void router.replace(
        buildClerkInviteHref("/signup", clerkTicket, "sign_up"),
      )
    } else if (clerkInviteStatus === "complete") {
      void router.replace("/home")
    }
  }, [clerkInviteStatus, clerkTicket, isLoaded, isSignedIn, router])

  const handleComplete = useCallback(() => {
    void router.replace(redirectUrl)
  }, [router, redirectUrl])

  // While Clerk resolves auth state, show the form skeleton instead of a blank
  // panel. Once loaded, a signed-in user (or invite redirect) is on its way
  // out, so render nothing rather than flashing the form.
  if (!isLoaded) {
    return <ClerkAuthFormSkeleton headingWidthClassName="w-36" />
  }

  if (isSignedIn || isInviteRedirect) {
    return null
  }

  return (
    <div className="space-y-6">
      <ClerkSigninForm
        onComplete={handleComplete}
        signUpHref={signUpHref}
        redirectUrlComplete={redirectUrl}
        invitationTicket={clerkTicket ?? undefined}
      />
    </div>
  )
}

SigninRoute.getLayout = (page: ReactElement) => (
  <SignupLayout>{page}</SignupLayout>
)

export default SigninRoute
